/**
 * Le zoo, cote client: apprivoiser, installer, rendre a la nature.
 *
 * POURQUOI CE MODULE EXISTE. Ces huit methodes vivaient dans `client/jeu.ts` et
 * y trainaient quatre champs de memorisation (`cleAccueil`, `accueilMemorise`,
 * `foyersCache`, `foyersSales`) que personne d'autre ne devait toucher — et que
 * n'importe quelle methode voisine pouvait pourtant lire. Ici les caches sont
 * PRIVES, et le seul moyen de les invalider est de passer par les gestes qui les
 * invalident vraiment.
 *
 * Les regles, elles, restent dans `shared/sim/zoo.ts` et `shared/sim/animaux.ts`:
 * ce module ne fait qu'orchestrer, prevenir le joueur et persister.
 */
import type { Animal, AnimalSauve, Batiment } from '../shared/types/monde.ts'
import type { Cout, Inventaire, ItemId } from '../shared/types/items.ts'
import type { Joueur } from '../shared/types/player.ts'
import type { ITerrain, Vec2 } from '../shared/types/world.ts'
import { hauteurSecuritaire } from '../shared/sim/terrain.ts'
import { Batiments } from '../shared/sim/monde-batiments.ts'
import { batimentContenant } from '../shared/sim/batiments.ts'
import { animalDef } from '../shared/data/animaux.ts'
import { CeNom, LeNom, Pronom, accorde, leComplement, pronom } from '../shared/sim/grammaire.ts'
import { REPAS_APPRIVOISEMENT, libererFoyer, nourrir } from '../shared/sim/animaux.ts'
import {
  adopter,
  enclosAuPoint,
  installerDansEnclos,
  peutAdopter,
  peutInstallerDansEnclos,
  type Enclos,
} from '../shared/sim/zoo.ts'
import { quantite } from '../shared/sim/inventaire.ts'
import type { FoyerRendu } from './render/zoo.ts'
import { audio } from './audio/audio.ts'
import { journal } from './log/journal.ts'

/** Ou un animal familier peut s'installer a l'endroit ou il se tient. */
export type AccueilZoo =
  | { genre: 'maison'; maison: Batiment; raison: null }
  | { genre: 'enclos'; enclos: Enclos; raison: null }
  | { genre: 'aucun'; raison: string | null }

export interface RappelsZoo {
  /** Previent le joueur. Le module n'a pas d'avis sur la duree ni le ton. */
  message(texte: string, duree: number, genre: 'info' | 'succes' | 'erreur'): void
  /** Persiste l'etat de cette bete (ou efface sa ligne si elle redevient banale). */
  sauverAnimal(a: Animal): void
  /** Retire un objet du sac, en passant par le serveur qui le tient. */
  depenser(cout: Cout): void
  /** Persiste le joueur: nourrir depense de l'energie. */
  sauverJoueur(): void
}

export class Zoo {
  private readonly terrain: ITerrain
  private readonly batiments: Batiments
  private readonly animaux: Animal[]
  private readonly token: string
  private readonly rappels: RappelsZoo

  /** Clef de memorisation de `accueilPour`: animal, position au metre, batiments. */
  private cleAccueil = ''
  private accueilMemorise: AccueilZoo = { genre: 'aucun', raison: null }
  /** Enclos habites, tels que le rendu les meuble. Refait quand ca change. */
  private foyersCache: FoyerRendu[] = []
  private foyersSales = true

  constructor(terrain: ITerrain, batiments: Batiments, animaux: Animal[], token: string, rappels: RappelsZoo) {
    this.terrain = terrain
    this.batiments = batiments
    this.animaux = animaux
    this.token = token
    this.rappels = rappels
  }

  /**
   * La friandise a tendre a cet animal: la premiere de ses gourmandises qu'on
   * ait dans le sac.
   *
   * L'appat n'a jamais ete un objet qu'on « equipe »: le champ `appat` existait
   * mais n'etait affecte nulle part, si bien que la reponse etait toujours null
   * et que le geste « nourrir » ne pouvait pas se declencher. Tout le
   * nourrissage etait donc du code mort — et avec lui, maintenant,
   * l'apprivoisement. On regarde donc simplement ce qu'on a sur soi.
   */
  friandisePour(a: Animal, inventaire: Inventaire): ItemId | null {
    for (const id of animalDef(a.kind).friandises) {
      if (quantite(inventaire, id) > 0) return id
    }
    return null
  }

  /** Vrai si cet animal est le sien: personne d'autre n'en dispose. */
  estSonAnimal(a: Animal): boolean {
    return a.ami === null || a.ami === this.token
  }

  /**
   * Ou cet animal pourrait s'installer, ici et maintenant.
   *
   * Le resultat est MEMORISE tant que l'animal n'a pas bouge d'un metre et que
   * rien n'a ete bati. La reconnaissance ne coute plus grand-chose depuis que
   * les enclos sont des boites du plan du zoo (elle etait un remplissage par
   * diffusion sur six mille cases), mais `peutAdopter` et `pensionnaires`
   * balaient tous les animaux, et l'infobulle se rafraichit a chaque image.
   */
  accueilPour(a: Animal): AccueilZoo {
    const cle = `${a.id}:${Math.round(a.pos.x)}:${Math.round(a.pos.z)}:${this.batiments.nombre}`
    if (cle === this.cleAccueil) return this.accueilMemorise
    const batiments = this.batiments.liste()
    let r: AccueilZoo

    const maison = batimentContenant(batiments, a.pos.x, a.pos.z)
    const vMaison = maison ? peutAdopter(a, maison, this.animaux, this.token) : null
    if (maison && vMaison?.ok) {
      r = { genre: 'maison', maison, raison: null }
    } else {
      const enclos = enclosAuPoint(batiments, a.pos.x, a.pos.z)
      const vEnclos = peutInstallerDansEnclos(a, enclos, this.animaux, this.token, this.terrain)
      if (vEnclos.ok && enclos) r = { genre: 'enclos', enclos, raison: null }
      // Dans une maison qui ne convient pas, c'est la maison qu'on commente:
      // parler de barriere a quelqu'un debout dans son salon n'aide personne.
      else r = { genre: 'aucun', raison: (maison ? vMaison?.raison : null) ?? vEnclos.raison ?? null }
    }

    this.cleAccueil = cle
    this.accueilMemorise = r
    return r
  }

  /** Tend une friandise. Trois repas espaces et l'animal devient familier. */
  donnerAManger(a: Animal, joueur: Joueur, tempsJeu: number): void {
    const friandise = this.friandisePour(a, joueur.inventaire)
    if (!friandise) {
      this.rappels.message(
        `${LeNom(animalDef(a.kind))} n’est pas ${accorde('intéressé', animalDef(a.kind))} par ce que tu as.`,
        2400,
        'info',
      )
      audio.erreurInterface()
      return
    }
    const r = nourrir(a, friandise, tempsJeu, this.token)
    if (!r.accepte) return
    // C'est le SERVEUR qui retire la friandise: le sac est tenu la-bas. Le
    // client ne fait que dire laquelle il tend.
    this.rappels.depenser({ [friandise]: 1 })
    audio.ramasser()
    const def = animalDef(a.kind)
    const nom = def.nom
    if (r.apprivoise) {
      this.rappels.message(
        `❤ ${LeNom(def)} est ${accorde('apprivoisé', def)} ! ${Pronom(def)} te suit maintenant.`,
        4200,
        'succes',
      )
    } else if (r.compte) {
      this.rappels.message(
        `${nom} ${accorde('nourri', def)} (${r.repas}/${REPAS_APPRIVOISEMENT}). Reviens ${leComplement(def)} voir plus tard.`,
        3000,
        'succes',
      )
    } else {
      this.rappels.message(`${LeNom(def)} mange, mais ${pronom(def)} n’a pas encore confiance…`, 3000, 'info')
    }
    journal.input('nourrir', `${a.kind} avec ${friandise}`, {
      repas: r.repas,
      compte: r.compte,
      apprivoise: r.apprivoise,
    })
    if (r.compte) this.rappels.sauverAnimal(a)
    this.rappels.sauverJoueur()
  }

  /**
   * Installe un familier chez lui, ou le rend a la nature s'il y habitait deja.
   *
   * On refuse en DISANT POURQUOI: sans phrase, un enclos ouvert d'un metre est
   * indiscernable d'un enclos ferme, et le joueur n'a aucun moyen de trouver
   * son erreur.
   */
  installerAnimal(a: Animal): void {
    const def = animalDef(a.kind)
    if (!this.estSonAnimal(a)) {
      this.rappels.message(`${CeNom(def)} a déjà son ami : ${pronom(def)} ne te suivra pas.`, 3000, 'erreur')
      audio.erreurInterface()
      return
    }

    if (a.ancre !== null) {
      libererFoyer(a)
      this.cleAccueil = ''
      this.foyersSales = true
      this.rappels.message(
        `${LeNom(def)} est ${accorde('rendu', def)} à la nature. ${Pronom(def)} reste ${def.feminin ? 'ton amie' : 'ton ami'}.`,
        3200,
        'info',
      )
      journal.input('zoo', `${a.kind} libere`)
      this.rappels.sauverAnimal(a)
      return
    }

    const accueil = this.accueilPour(a)
    if (accueil.genre === 'aucun') {
      this.rappels.message(accueil.raison ?? 'Il ne peut pas s’installer ici.', 3600, 'erreur')
      audio.erreurInterface()
      journal.input('zoo', `installation refusee pour ${a.kind}: ${accueil.raison ?? 'sans raison'}`)
      return
    }

    if (accueil.genre === 'maison') {
      adopter(a, accueil.maison, this.token)
      this.rappels.message(`🏠 ${LeNom(def)} habite maintenant chez toi !`, 4000, 'succes')
      journal.input('zoo', `${a.kind} adopte dans ${accueil.maison.kind}`, { maison: accueil.maison.id })
    } else {
      installerDansEnclos(a, accueil.enclos, this.token)
      const e = accueil.enclos
      this.rappels.message(`🦌 ${LeNom(def)} s’installe dans « ${e.nom} ».`, 4000, 'succes')
      journal.input('zoo', `${a.kind} installe dans un enclos`, {
        enclos: e.nom,
        index: e.index,
        zoo: e.zoo.id,
        capacite: e.capacite,
      })
    }
    this.cleAccueil = ''
    this.foyersSales = true
    audio.construire()
    this.rappels.sauverAnimal(a)
  }

  /**
   * Applique la sauvegarde des animaux apprivoises sur la faune regeneree.
   *
   * Les identifiants d'animaux sont des index de creation: ils ne valent que
   * tant que la generation ne bouge pas. On verifie donc l'espece avant
   * d'appliquer, sinon un changement de population transformerait le lapin de
   * la maison en ours polaire.
   */
  appliquerSauvegarde(liste: readonly AnimalSauve[], batiments: readonly Batiment[]): void {
    const maisons = new Set(batiments.map((b) => b.id))
    let repris = 0
    for (const s of liste) {
      const a = this.animaux[s.animalId]
      if (!a || a.kind !== s.kind) {
        journal.warn('zoo', `animal ${s.animalId} ignore: ${s.kind} attendu, ${a?.kind ?? 'aucun'} trouve`)
        continue
      }
      a.repas = s.repas
      a.dernierRepas = s.dernierRepas
      a.ami = s.ami.length > 0 ? s.ami : null
      // Il reprend la ou il etait: un animal de compagnie ne renait pas a
      // l'autre bout de la carte, la ou la graine l'avait pose.
      a.pos.x = s.pos.x
      a.pos.z = s.pos.z
      a.y = this.terrain.hauteur(s.pos.x, s.pos.z)
      // Une maison disparue pendant notre absence (demolie, monde remis a
      // zero) ne doit pas laisser un animal tourner autour d'un terrain vague:
      // il retourne a la nature, et il reste familier.
      const maison = s.maison.length > 0 ? s.maison : null
      if (maison !== null && !maisons.has(maison)) {
        journal.warn('zoo', `${a.kind} rendu a la nature: sa maison ${maison} n'existe plus`)
      } else if (s.ancre) {
        a.ancre = { x: s.ancre.x, z: s.ancre.z }
        a.rayon = s.rayon
        a.maison = maison
      }
      repris++
    }
    if (repris > 0) journal.info('zoo', `${repris} animaux apprivoises repris`)
    this.foyersSales = true
  }

  /**
   * Les enclos habites, pour que le rendu y pose l'abreuvoir et l'abri.
   *
   * Le resultat est garde: la boucle de rendu appelle cette methode a chaque
   * image, et balayer trois cents animaux pour reconstruire un tableau de deux
   * entrees soixante fois par seconde ne nourrirait que le ramasse-miettes.
   */
  foyers(): FoyerRendu[] {
    if (!this.foyersSales) return this.foyersCache
    this.foyersSales = false
    const sortie: FoyerRendu[] = []
    for (const a of this.animaux) {
      if (a.ancre === null || a.maison !== null) continue
      // Deux pensionnaires du meme enclos partagent la meme ancre au metre
      // pres: on ne meuble qu'une fois.
      if (sortie.some((f) => Math.abs(f.x - (a.ancre as Vec2).x) < 1 && Math.abs(f.z - (a.ancre as Vec2).z) < 1)) {
        continue
      }
      sortie.push({
        x: a.ancre.x,
        z: a.ancre.z,
        y: hauteurSecuritaire(this.terrain, a.ancre.x, a.ancre.z),
        rayon: a.rayon,
      })
    }
    this.foyersCache = sortie
    return sortie
  }

  /**
   * Rend a la nature les animaux d'une maison demolie.
   *
   * Sans ca, l'animal continuait de tourner autour d'un tas de planches
   * disparu, et sa ligne en base gardait une maison qui n'existe plus.
   */
  libererAnimauxDe(idMaison: string, sauver = true): void {
    for (const a of this.animaux) {
      if (a.maison !== idMaison) continue
      libererFoyer(a)
      this.cleAccueil = ''
      this.foyersSales = true
      journal.info('zoo', `${a.kind} rendu a la nature: sa maison a ete demolie`)
      // Quand c'est un AUTRE joueur qui a demoli, son client a deja prevenu le
      // serveur: le refaire ici renverrait la meme nouvelle a tout le monde.
      if (sauver) this.rappels.sauverAnimal(a)
    }
  }

  /**
   * Quelqu'un d'autre a rendu cette bete a la nature: on l'applique sans rien
   * reecrire cote serveur, l'annonce vient justement de la.
   */
  libererDepuisReseau(animalId: number): void {
    const a = this.animaux[animalId]
    if (!a) return
    libererFoyer(a)
    a.repas = 0
    a.ami = null
    this.cleAccueil = ''
    this.foyersSales = true
  }
}
